import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, CheckCircle2, XCircle, Printer } from "lucide-react";

interface VerificationData {
  id: string;
  user_id: string;
  area_of_expertise: string;
  specialization: string;
  years_of_experience: number;
  education: string;
  certifications: string;
  additional_notes: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
  user_profile?: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

const TherapistVerificationsTable = () => {
  const [verifications, setVerifications] = useState<VerificationData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVerification, setSelectedVerification] = useState<VerificationData | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    fetchVerifications();
    // Set up real-time subscription
    const subscription = supabase
      .channel('therapist_verifications_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'therapist_verifications' 
        }, 
        () => {
          fetchVerifications();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchVerifications = async () => {
    try {
      const { data, error } = await supabase
        .from('therapist_verifications')
        .select(`
          *,
          user_profile:user_profiles(first_name, last_name, email)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setVerifications((data || []) as VerificationData[]);
    } catch (error) {
      console.error('Error fetching verifications:', error);
      toast.error('Failed to load verification data');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (verificationId: string, newStatus: 'approved' | 'rejected') => {
    try {
      const { error } = await supabase
        .from('therapist_verifications')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', verificationId);

      if (error) throw error;
      toast.success(`Verification ${newStatus}`);
      fetchVerifications();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update verification status');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      approved: { color: 'bg-green-100 text-green-800', label: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' }
    };
    const config = statusConfig[status as keyof typeof statusConfig];
    return (
      <Badge className={config.color}>
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-[#3BAFDA]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Therapist</TableHead>
            <TableHead>Area of Expertise</TableHead>
            <TableHead>Specialisation</TableHead>
            <TableHead>Years</TableHead>
            <TableHead>Education</TableHead>
            <TableHead>Certifications</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {verifications.map((verification) => (
            <TableRow key={verification.id}>
              <TableCell>
                {verification.user_profile?.first_name} {verification.user_profile?.last_name}
                <div className="text-sm text-[#5A636E]">{verification.user_profile?.email}</div>
              </TableCell>
              <TableCell>{verification.area_of_expertise}</TableCell>
              <TableCell>{verification.specialization}</TableCell>
              <TableCell>{verification.years_of_experience}</TableCell>
              <TableCell className="max-w-[180px] truncate">{verification.education}</TableCell>
              <TableCell className="max-w-[180px] truncate">{verification.certifications}</TableCell>
              <TableCell>{getStatusBadge(verification.status)}</TableCell>
              <TableCell>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedVerification(verification);
                      setIsDialogOpen(true);
                    }}
                  >
                    View Details
                  </Button>
                  {verification.status === 'pending' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-green-600 hover:bg-green-50"
                        onClick={() => handleStatusUpdate(verification.id, 'approved')}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleStatusUpdate(verification.id, 'rejected')}
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                    </>
                  )}
                  {verification.status === 'approved' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleStatusUpdate(verification.id, 'rejected')}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  )}
                  {verification.status === 'rejected' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-green-600 hover:bg-green-50"
                      onClick={() => handleStatusUpdate(verification.id, 'approved')}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Verification Details</DialogTitle>
          </DialogHeader>
          {selectedVerification && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold">Therapist Information</h3>
                <p>Name: {selectedVerification.user_profile?.first_name} {selectedVerification.user_profile?.last_name}</p>
                <p>Email: {selectedVerification.user_profile?.email}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold">Area of Expertise</h3>
                  <p>{selectedVerification.area_of_expertise}</p>
                  <h3 className="font-semibold mt-4">Specialisation</h3>
                  <p>{selectedVerification.specialization}</p>
                  <h3 className="font-semibold mt-4">Years of Experience</h3>
                  <p>{selectedVerification.years_of_experience}</p>
                </div>
                <div>
                  <h3 className="font-semibold">Education</h3>
                  <p className="whitespace-pre-wrap">{selectedVerification.education}</p>
                  <h3 className="font-semibold mt-4">Certifications</h3>
                  <p className="whitespace-pre-wrap">{selectedVerification.certifications}</p>
                </div>
              </div>
              {selectedVerification.additional_notes && (
                <div>
                  <h3 className="font-semibold">Additional Notes</h3>
                  <p className="whitespace-pre-wrap">{selectedVerification.additional_notes}</p>
                </div>
              )}
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    const printWindow = window.open('', '_blank');
                    if (printWindow) {
                      printWindow.document.write(`
                        <html>
                          <head>
                            <title>Therapist Verification Details</title>
                            <style>
                              body { font-family: Arial, sans-serif; padding: 20px; }
                              h2 { color: #4B5563; margin-bottom: 20px; }
                              h3 { color: #6B7280; margin-top: 20px; margin-bottom: 10px; }
                              p { margin: 5px 0; }
                              .status { 
                                display: inline-block;
                                padding: 4px 8px;
                                border-radius: 4px;
                                font-weight: bold;
                                margin-bottom: 20px;
                              }
                              .status-${selectedVerification.status} {
                                background-color: ${
                                  selectedVerification.status === 'approved' ? '#D1FAE5' :
                                  selectedVerification.status === 'rejected' ? '#FEE2E2' :
                                  '#FEF3C7'
                                };
                                color: ${
                                  selectedVerification.status === 'approved' ? '#065F46' :
                                  selectedVerification.status === 'rejected' ? '#991B1B' :
                                  '#92400E'
                                };
                              }
                            </style>
                          </head>
                          <body>
                            <h2>Therapist Verification Details</h2>
                            <div style="margin-bottom: 20px;">
                              <h3>Therapist Information</h3>
                              <p>Name: ${selectedVerification.user_profile?.first_name} ${selectedVerification.user_profile?.last_name}</p>
                              <p>Email: ${selectedVerification.user_profile?.email}</p>
                            </div>
                            <div class="status status-${selectedVerification.status}">
                              Status: ${selectedVerification.status.toUpperCase()}
                            </div>
                            <div>
                              <h3>Area of Expertise</h3>
                              <p>${selectedVerification.area_of_expertise}</p>
                              <h3>Specialisation</h3>
                              <p>${selectedVerification.specialization}</p>
                              <h3>Years of Experience</h3>
                              <p>${selectedVerification.years_of_experience}</p>
                            </div>
                            <div>
                              <h3>Education</h3>
                              <p>${selectedVerification.education}</p>
                              <h3>Certifications</h3>
                              <p>${selectedVerification.certifications}</p>
                            </div>
                            ${selectedVerification.additional_notes ? `
                              <div>
                                <h3>Additional Notes</h3>
                                <p>${selectedVerification.additional_notes}</p>
                              </div>
                            ` : ''}
                            <div style="margin-top: 20px; font-size: 12px; color: #6B7280;">
                              Printed on: ${new Date().toLocaleString()}
                            </div>
                          </body>
                        </html>
                      `);
                      printWindow.document.close();
                      printWindow.print();
                    }
                  }}
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print Details
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TherapistVerificationsTable; 